import type { CompanyConfigProfileInput } from "../../generated/core-api";
import {
  emptyProfile,
  emptyPayGroupProfile,
  emptyCompanySettingsProfile,
  emptyPayGroupSettingsProfile,
  PAY_GROUP_FIELDS,
  OPTION_SECTIONS,
  GROUPS,
  GROUP_LABELS,
  CS_BOOLEAN_DEPENDENTS,
  CS_BOOLEAN_PARENT,
  enabledSections,
} from "./companyOptions";

export * from "./companyShellCsv";
export * from "./companyShellValidation";
export * from "./companyShellCommit";
export { fieldsForGroup } from "./optionHelpers";

// ── Re-exports from companyOptions ────────────────────────────────────────────

// Types (re-export so consumers import from the module barrel):
export type {
  OptionGroup,
  OptionFieldDef,
  OptionSection,
  CompanyProfileBooleanKey,
  PayGroupBooleanKey,
  PayGroupNumberKey,
  PgNumberErrors,
  CsErrors,
  CompanySettingsBooleanKey,
  CompanySettingsStringKey,
  PayGroupSettingsBooleanKey,
} from "./companyOptions";

// Values (imported above for local use; re-export for external consumers):
export {
  emptyProfile,
  emptyPayGroupProfile,
  emptyCompanySettingsProfile,
  emptyPayGroupSettingsProfile,
  PAY_GROUP_FIELDS,
  OPTION_SECTIONS,
  GROUPS,
  GROUP_LABELS,
  CS_BOOLEAN_DEPENDENTS,
  CS_BOOLEAN_PARENT,
  enabledSections,
};

// ── Company config catalog — display name (R-GOV-13 / AC-GOV-17) ─────────────

/** Matches `CompanyConfigCreate.display_name` max_length on Pacific.Agentic.Api. */
export const COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH = 255;

export function displayNameValidationError(
  displayName: string,
  options?: { enforceMaxLength?: boolean },
): string {
  const trimmed = displayName.trim();
  if (!trimmed) {
    return "Display name is required.";
  }
  if (
    options?.enforceMaxLength !== false &&
    trimmed.length > COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH
  ) {
    return `Display name must be ${COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`;
  }
  return "";
}

// ── Form shape ────────────────────────────────────────────────────────────────

/**
 * The mutable form shape for the profile. Booleans default to `false`; the two
 * special non-boolean fields are represented as empty/`null` until edited.
 */
/**
 * No `config_code` field (R-GOV-15 / Web#431): the opaque id is server-minted on
 * create and comes from `initial.config_code` on edit — never from form state.
 */
export interface MaintainCompanyConfigFormValue {
  display_name: string;
  change_description: string;
  profile: CompanyConfigProfileInput;
}

/** A fresh, empty "Maintain company configuration" form value. */
export function emptyMaintainCompanyConfigForm(): MaintainCompanyConfigFormValue {
  return {
    display_name: "",
    change_description: "",
    profile: {
      CompanyOptions: emptyProfile(),
      // CompanySettings and PayGroupSettings are included so new-form fields
      // have a valid starting state. cleanProfile() strips them back out before
      // submit when they remain all-defaults (no user edits), keeping create
      // payloads free of empty sub-profile objects.
      CompanySettings: emptyCompanySettingsProfile(),
      PayGroupSettings: emptyPayGroupSettingsProfile(),
    },
  };
}
